// 276 / 277 — asking what happened to a claim, and being told.
//
// This is the transaction that ends manual ICN entry. Today somebody opens
// GAMMIS, finds the claim, reads the Internal Control Number off the screen and
// types it into CAERA — per claim, by hand, which is both the slowest part of
// the billing week and the one most likely to be skipped. The ICN matters
// because it is the payer's handle on the claim: without it a denial cannot be
// appealed and a payment cannot be traced.
//
// The 277 carries it in REF*1K, and it carries it against the trace number we
// chose. That is the whole trick: 2200D-TRN02 is documented as "value received
// on the 276 will be returned on the 277", so we send the claim's control
// number as the trace and get it back beside the ICN.
//
// ONE IDENTIFIER, END TO END. claim_lines.claim_control_number is what goes in
//
//   837P  CLM01        the claim as filed
//   824   CTX          which claim was rejected
//   276   TRN02        which claim we are asking about
//   277   TRN02        which claim is being answered
//   835   CLP01        which claim was paid
//
// so every document in the lifecycle can be tied to the line it concerns by a
// lookup rather than by matching dates and amounts and hoping. That property
// is the reason to keep the control number stable, and the reason not to
// "tidy" it into something shorter.
//
// From GAMMIS_5010_276-277_Companion_Guide_v2.9: AMT*T3 and DTP*472 are both
// marked "GAMMIS requires this segment to refine the search criteria", the
// member is always the subscriber (there is never a dependent level), and the
// worked example in §11.3 is what the builder below reproduces.

import {
  seg, d8, t4, parse,
  interchange, functionalGroup, transaction,
  GAMMIS_TRADING_PARTNER_ID,
} from './x12.js';
import { requireIdentity } from './billingIdentity.js';
import { GAMMIS_NAME } from './x837p.js';

export const IMPLEMENTATION_REF = '005010X212';

/**
 * Claim status category codes (X12 code source 507), reduced to the decision
 * they imply. Only the first character carries the outcome; the digit after it
 * narrows the reason, and the full pair is kept for display.
 *
 * A4 and D0 are called out separately because they do not mean "still
 * working". They mean GAMMIS has no record of the claim — which, for a claim
 * we believe we filed, is the most important thing a status inquiry can ever
 * tell us and the easiest to lose in a bucket called "pending".
 */
export const STATUS_CATEGORY = {
  A0: 'acknowledged', A1: 'acknowledged', A2: 'acknowledged', A5: 'acknowledged',
  A3: 'unprocessable', A6: 'unprocessable', A7: 'unprocessable', A8: 'unprocessable',
  A4: 'not_found', D0: 'not_found',
  P0: 'pending', P1: 'pending', P2: 'pending', P3: 'pending', P4: 'pending', P5: 'pending',
  F0: 'finalized', F1: 'paid', F2: 'denied', F3: 'adjusted', F3F: 'forwarded', F4: 'no_payment',
  E0: 'request_error', E1: 'request_error', E2: 'request_error', E3: 'request_error', E4: 'request_error',
};

export const CATEGORY_MEANING = {
  acknowledged: 'Received and in adjudication.',
  pending: 'Pending — GAMMIS is still working on it.',
  paid: 'Finalized and paid.',
  denied: 'Finalized and denied.',
  adjusted: 'Finalized, and the original claim was revised.',
  no_payment: 'Adjudication complete, no payment forthcoming.',
  finalized: 'Finalized.',
  forwarded: 'Forwarded to another payer.',
  unprocessable: 'Returned as unprocessable — it never reached adjudication.',
  not_found: 'GAMMIS has no record of this claim. It was never received, or it was filed under details that do not match.',
  request_error: 'The inquiry itself was rejected, not the claim.',
  unknown: 'Status returned that this does not recognise.',
};

/** Whether a status means GAMMIS has finished with the claim. */
export const isSettled = (category) =>
  ['paid', 'denied', 'adjusted', 'no_payment', 'finalized'].includes(category);

/**
 * Build a batch 276.
 *
 * `inquiries` is one entry per claim: { controlNumber, memberId, firstName,
 * lastName, dob, gender, chargeAmount, serviceDateFrom, serviceDateTo }.
 * They are grouped by member, because 2200D repeats inside one subscriber loop
 * and sending the same member three times would be three searches where one
 * would do.
 */
export const build276 = ({
  identity, inquiries = [], at = new Date(), usageIndicator = 'T',
  controlNumber,
  batchRef,
}) => {
  requireIdentity(identity, { forTransmission: true });
  if (!inquiries.length) throw new Error('No claims to ask about.');

  const ref = batchRef || `CS${d8(at)}${t4(at)}`;
  const body = [
    seg('BHT', '0010', '13', ref, d8(at), t4(at)),
    // 2000A — information source, the payer.
    seg('HL', '1', '', '20', '1'),
    seg('NM1', 'PR', '2', GAMMIS_NAME, '', '', '', '', 'PI', GAMMIS_TRADING_PARTNER_ID),
    // 2000B — information receiver, us as a trading partner.
    seg('HL', '2', '1', '21', '1'),
    seg('NM1', '41', '2', identity.legalName, '', '', '', '', '46', identity.tradingPartnerId),
    // 2000C — service provider, by NPI.
    seg('HL', '3', '2', '19', '1'),
    seg('NM1', '1P', '2', identity.legalName, '', '', '', '', 'XX', identity.npi),
  ];

  // Group by member. A member with three claims is one subscriber loop with
  // three 2200D blocks, not three subscriber loops.
  const byMember = new Map();
  for (const q of inquiries) {
    const key = String(q.memberId || '').trim();
    if (!key) throw new Error(`Claim ${q.controlNumber} has no Medicaid ID, so GAMMIS cannot be asked about it.`);
    if (!byMember.has(key)) byMember.set(key, []);
    byMember.get(key).push(q);
  }

  let hl = 4;
  for (const [memberId, claims] of byMember) {
    const first = claims[0];
    // HL04 is 0: for Georgia Medicaid the member is always the subscriber and
    // there is never a dependent level (companion guide, 2000D-HL04).
    body.push(seg('HL', String(hl++), '3', '22', '0'));
    if (first.dob) {
      body.push(seg('DMG', 'D8', d8(first.dob), (first.gender || '').toUpperCase().slice(0, 1) || 'U'));
    }
    body.push(seg('NM1', 'IL', '1', first.lastName, first.firstName, '', '', '', 'MI', memberId));

    for (const q of claims) {
      body.push(seg('TRN', '1', q.controlNumber));
      // Both of these are marked required by GAMMIS "to refine the search
      // criteria for a specific claim" — without them the search is by member
      // and returns everything, or nothing.
      body.push(seg('AMT', 'T3', money(q.chargeAmount)));
      const from = d8(q.serviceDateFrom);
      const to = d8(q.serviceDateTo || q.serviceDateFrom);
      body.push(from === to
        ? seg('DTP', '472', 'D8', from)
        : seg('DTP', '472', 'RD8', `${from}-${to}`));
    }
  }

  return interchange({
    senderId: identity.tradingPartnerId,
    controlNumber,
    usageIndicator, at,
    groups: [functionalGroup({
      functionalId: 'HR',
      senderId: identity.tradingPartnerId,
      controlNumber,
      version: IMPLEMENTATION_REF,
      at,
      transactions: [transaction({
        code: '276', controlNumber,
        implementationRef: IMPLEMENTATION_REF, body,
      })],
    })],
  });
};

const money = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : '0.00';
};

const isoFrom = (raw = '') => {
  const s = String(raw);
  if (/^\d{8}$/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  const m = s.match(/^(\d{8})-(\d{8})$/);
  return m ? isoFrom(m[1]) : null;
};

const readStatus = (raw, delimiter) => {
  const parts = String(raw || '').split(delimiter);
  const category = (parts[0] || '').toUpperCase();
  return {
    categoryCode: category,
    statusCode: parts[1] || '',
    entityCode: parts[2] || '',
    category: STATUS_CATEGORY[category] || 'unknown',
  };
};

/**
 * Parse a 277 into one result per claim.
 *
 * The trace is the key. Everything else — the ICN, the status, the amounts —
 * hangs off the claim whose control number we sent, so a caller never has to
 * guess which line a status belongs to.
 *
 * A 277 with no TRN is not an error: GAMMIS answers a rejected inquiry at
 * provider level (2200C), and that carries the reason without naming a claim.
 * Those come back in `problems` rather than being dropped.
 */
export const parse277 = (text) => {
  const { segments, delimiters } = parse(text);
  if (!segments.length) return null;
  const comp = delimiters?.component || ':';

  const results = [];
  const problems = [];
  let current = null;
  let member = null;
  let onServiceLine = false;

  const close = () => { if (current) results.push(current); current = null; onServiceLine = false; };

  for (const s of segments) {
    const id = s[0];

    if (id === 'HL') {
      // A new hierarchical level ends whatever claim was being described.
      close();
      if (s[3] !== '22') member = null;
      continue;
    }

    if (id === 'NM1' && s[1] === 'IL') {
      member = {
        lastName: s[3] || '', firstName: s[4] || '',
        memberId: s[9] || '',
      };
      continue;
    }

    if (id === 'TRN') {
      close();
      current = {
        // TRN02 is the value we sent as the claim's control number.
        controlNumber: s[2] || '',
        member,
        icn: '', claimIdentifier: '', patientAccount: '',
        status: null, additionalStatuses: [],
        statusDate: null, chargeAmount: null, paidAmount: null,
        serviceDateFrom: null, serviceLines: [],
      };
      continue;
    }

    if (id === 'STC') {
      const status = readStatus(s[1], comp);
      // STC10 and STC11 are a second and third status, used when one code
      // cannot say the whole thing.
      const extra = [s[10], s[11]].filter(Boolean).map((raw) => readStatus(raw, comp));

      if (onServiceLine && current?.serviceLines.length) {
        const line = current.serviceLines[current.serviceLines.length - 1];
        line.status = status;
        line.additionalStatuses = extra;
        continue;
      }
      if (!current) {
        // Provider-level (2200C) — the inquiry was rejected before any claim
        // was looked for.
        problems.push({
          ...status,
          message: CATEGORY_MEANING[status.category] || CATEGORY_MEANING.unknown,
        });
        continue;
      }
      current.status = status;
      current.additionalStatuses = extra;
      current.statusDate = isoFrom(s[2]);
      if (s[4] !== undefined && s[4] !== '') current.chargeAmount = Number(s[4]);
      if (s[5] !== undefined && s[5] !== '') current.paidAmount = Number(s[5]);
      continue;
    }

    if (id === 'REF' && current) {
      // The point of the whole exercise.
      if (s[1] === '1K') current.icn = s[2] || '';
      if (s[1] === 'D9') current.claimIdentifier = s[2] || '';
      if (s[1] === 'EJ') current.patientAccount = s[2] || '';
      continue;
    }

    if (id === 'SVC' && current) {
      const proc = String(s[1] || '').split(comp);
      current.serviceLines.push({
        serviceCode: proc[1] || proc[0] || '',
        modifiers: proc.slice(2).filter(Boolean),
        billed: Number(s[2] ?? 0),
        paid: Number(s[3] ?? 0),
        units: s[7] !== undefined ? Number(s[7]) : null,
        status: null, additionalStatuses: [],
      });
      onServiceLine = true;
      continue;
    }

    if (id === 'DTP' && s[1] === '472' && current) {
      const iso = isoFrom(s[3]);
      if (!onServiceLine) current.serviceDateFrom = iso;
      continue;
    }
  }
  close();

  return { results, problems };
};

/**
 * What a 277 result should change on a claim line.
 *
 * TWO RULES, AND THE SECOND IS THE IMPORTANT ONE.
 *
 * The ICN is always written. That is what the inquiry is for, it is the
 * payer's handle on the claim, and a later answer carrying the same ICN says
 * the same thing.
 *
 * The status is written only while the line is still `submitted`. A 277 is a
 * status document, not a payment document: it reports what GAMMIS thinks, but
 * the money comes from a remittance. Once an 835 or an RA has posted a payment
 * or a denial, letting a status inquiry rewrite that would give two sources for
 * one fact, and they would eventually disagree. `paid_amount` is never touched
 * here, even when STC05 carries one.
 */
export const updateFromStatus = (result, line, { at = new Date() } = {}) => {
  const update = {};
  if (result.icn && result.icn !== line?.icn) update.icn = result.icn;

  const category = result.status?.category || 'unknown';

  // What GAMMIS last said, kept whether or not it changed anything. A claim
  // sitting at `submitted` because it is still pending and one sitting there
  // because GAMMIS has never heard of it look identical on the line otherwise,
  // and only one of them is somebody's problem today.
  if (category !== 'unknown' || result.status) {
    update.last_status_category = category;
    update.last_status_code = [result.status?.categoryCode, result.status?.statusCode]
      .filter(Boolean).join(':') || null;
    update.last_status_at = at.toISOString();
  }

  if (line?.status === 'submitted') {
    if (category === 'paid') update.status = 'paid';
    else if (category === 'denied' || category === 'no_payment') update.status = 'denied';
    else if (category === 'adjusted') update.status = 'adjusted';
  }
  return update;
};

/** A sentence about this claim, for a person reading the run. */
export const explainStatus = (result) => {
  const category = result.status?.category || 'unknown';
  const base = CATEGORY_MEANING[category] || CATEGORY_MEANING.unknown;
  const code = result.status?.categoryCode
    ? ` (${result.status.categoryCode}${result.status.statusCode ? `:${result.status.statusCode}` : ''})`
    : '';
  return base + code;
};

/** Which results a person needs to look at rather than just record. */
export const statusAnomalies = (results = []) =>
  results
    .filter((r) => ['not_found', 'unprocessable', 'request_error'].includes(r.status?.category))
    .map((r) => ({
      controlNumber: r.controlNumber,
      category: r.status.category,
      message: explainStatus(r),
    }));
